<template>
  <div class="gallery">
    <TnHeading type="h1" size="l" class="margin-bottom-2xs">UI preview</TnHeading>
    <TnParagraph size="s" class="margin-bottom-l">
      Open any screen directly, without walking through the flow. Screens that call a backend will
      show their error state locally &mdash; the layout still renders.
    </TnParagraph>

    <div class="gallery-body">
      <!-- Catalog -->
      <nav class="gallery-nav">
        <section v-for="area in uiPreviewAreas" :key="area.key" class="margin-bottom-l">
          <TnHeading type="h2" size="3xs">{{ area.label }}</TnHeading>
          <TnParagraph size="s" class="margin-bottom-2xs gallery-muted">
            {{ area.description }}
          </TnParagraph>

          <ul class="gallery-list">
            <li v-for="entry in area.entries" :key="`${area.key}-${entry.label}`">
              <button
                type="button"
                class="gallery-item"
                :class="{ 'is-selected': selected?.label === entry.label }"
                @click="select(entry)"
              >
                {{ entry.label }}
                <span class="gallery-item-path">{{ resolvePath(entry) }}</span>
              </button>
            </li>
          </ul>
        </section>
      </nav>

      <!-- Preview -->
      <section class="gallery-preview">
        <div v-if="!selected" class="gallery-empty">
          <TnParagraph size="s">Select a screen to preview it.</TnParagraph>
        </div>

        <div v-else>
          <div class="margin-bottom-s gallery-toolbar">
            <TnLink size="s" :href="previewUrl" target="_blank" :text="'Open in new tab'" />
          </div>

          <TnParagraph v-if="selected.note" size="s" class="margin-bottom-2xs gallery-muted">
            {{ selected.note }}
          </TnParagraph>

          <div class="gallery-frame">
            <iframe
              :key="frameKey"
              :src="previewUrl"
              :style="{ width: MOBILE_VIEWPORT_WIDTH, height: PREVIEW_HEIGHT }"
              class="gallery-iframe"
              title="UI preview"
            />
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { TnHeading, TnLink, TnParagraph } from '@telenornorgeinternal/telegrafen-vue3-core';

import { uiPreviewAreas, type UiPreviewEntry } from './uiCatalog';

const router = useRouter();

const selected = ref<UiPreviewEntry | null>(null);
const frameKey = ref(0);

/** Previews are always rendered at mobile width. */
const MOBILE_VIEWPORT_WIDTH = '390px';

/**
 * Tall enough for a full screen, but capped to the viewport so the sticky
 * preview (toolbar included) always fits without scrolling the panel itself.
 */
const PREVIEW_HEIGHT = 'min(820px, calc(100vh - 160px))';

/** Resolves an entry to its in-app path (without the router base). */
function resolvePath(entry: UiPreviewEntry): string {
  return router.resolve({ name: entry.routeName, query: entry.query }).path;
}

/** Full URL including the '/ui/' base, for the iframe and the new-tab link. */
const previewUrl = computed(() => {
  if (!selected.value) return '';
  return router.resolve({ name: selected.value.routeName, query: selected.value.query }).href;
});

function select(entry: UiPreviewEntry) {
  selected.value = entry;
  reload();
}

function reload() {
  frameKey.value += 1;
}
</script>

<style scoped>
.gallery {
  max-width: 1280px;
  margin-inline: auto;
  padding: 24px;
}

.gallery-body {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.gallery-nav {
  width: 100%;
  flex-shrink: 0;
}

.gallery-muted {
  color: #525252;
}

.gallery-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  list-style: none;
}

.gallery-item {
  display: block;
  width: 100%;
  padding: 8px 12px;
  border: 0;
  border-radius: 6px;
  background-color: transparent;
  color: inherit;
  font: inherit;
  font-size: 14px;
  line-height: 20px;
  text-align: left;
  cursor: pointer;
}

.gallery-item:hover,
.gallery-item.is-selected {
  background-color: #f5f5f5;
}

.gallery-item.is-selected {
  font-weight: 500;
}

.gallery-item-path {
  display: block;
  font-size: 12px;
  line-height: 16px;
  color: #737373;
}

.gallery-preview {
  flex: 1;
  min-width: 0;
}

.gallery-empty {
  padding: 32px;
  border-radius: 6px;
  background-color: #fafafa;
  text-align: center;
}

.gallery-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.gallery-frame {
  overflow: hidden;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background-color: #fff;
}

.gallery-iframe {
  display: block;
  margin-inline: auto;
  border: 0;
}

@media (min-width: 1024px) {
  .gallery-body {
    flex-direction: row;
    align-items: flex-start;
  }

  .gallery-nav {
    width: 384px;
  }

  /* Keep the preview (toolbar + iframe) in view while scrolling the catalog. */
  .gallery-preview {
    position: sticky;
    top: 24px;
    align-self: flex-start;
    max-height: calc(100vh - 48px);
    overflow-y: auto;
  }
}
</style>
